/** @format */

import {Box} from '@mui/material';
import {type Theme} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';

import {shortNumber} from 'helpers/numberHelpers';
import {type MetricProgress} from 'features/mediaplan-progress/types';

import {MediaplanCta} from '../KpiSection/MediaplanCta';
import {
  DataCell,
  ForecastHeaderCell,
  FullDivider,
  Grid,
  HeaderCell,
  HeaderSubText,
  HeaderText,
  RowDivider,
  Root,
  ValueText,
} from './styles';

interface Props {
  progress: MetricProgress;
  onSetup: () => void;
  // false when <7 days of fact → the «Прогноз» column is dropped (AS-7886), matching the rest
  // of the body (SpendSection / KpiTable / KpiCharts gate the forecast the same way).
  showForecast: boolean;
}

const formatMoney = (value: number): string => `${shortNumber(value)} ₽`;

/**
 * Summary-mode body when the project has no mediaplan (Figma 10286:128462): a narrow
 * bordered box with a reduced Расход table — Показатель | Факт на сегодня | Прогноз
 * (no plan columns, since there is no plan) — and the «Настроить медиаплан» CTA hatch
 * below it inside the same box.
 */
export function NoMediaplanSummary({progress, onSetup, showForecast}: Props) {
  const {t} = useTranslation();
  const {factToDate, forecastAtEnd} = progress;

  const onSurface = (theme: Theme) => theme.palette.semantic_dark?.surface.onSurface;

  const columns = showForecast ? 3 : 2;

  return (
    <Root>
      <Grid columns={columns}>
        {/* header */}
        <HeaderCell>
          <HeaderText>
            <Trans>Показатель</Trans>
          </HeaderText>
        </HeaderCell>
        <HeaderCell>
          <HeaderText>
            <Trans>Факт</Trans>
          </HeaderText>
          <HeaderSubText>
            <Trans>на сегодня</Trans>
          </HeaderSubText>
        </HeaderCell>
        {showForecast && (
          <ForecastHeaderCell>
            <Box component='span' className='material-symbols-outlined'>
              event_upcoming
            </Box>
            <HeaderText>{t('Прогноз')}</HeaderText>
          </ForecastHeaderCell>
        )}

        <RowDivider columns={columns} />

        {/* Расход row (no plan → only fact + forecast) */}
        <DataCell sx={{gap: 1}}>
          <Box
            component='span'
            className='material-symbols-outlined'
            sx={{
              '&.material-symbols-outlined': {fontSize: '18px', lineHeight: '20px'},
              color: onSurface,
            }}
          >
            currency_ruble
          </Box>
          <ValueText sx={{color: onSurface}}>
            <Trans>Расход</Trans>
          </ValueText>
        </DataCell>
        <DataCell>
          <ValueText sx={{color: onSurface}}>{formatMoney(factToDate)}</ValueText>
        </DataCell>
        {showForecast && (
          <DataCell>
            <ValueText sx={{color: onSurface}}>{formatMoney(forecastAtEnd)}</ValueText>
          </DataCell>
        )}
      </Grid>

      <FullDivider />

      <MediaplanCta fullWidth onSetup={onSetup} />
    </Root>
  );
}
